<template>
  <div class="home-page" :style="homeThemeVars">
    <!-- Desktop layout -->
    <div class="home-page__layout home-page__layout--desktop">
      <aside
        class="home-page__sidebar booking-brand-surface"
        :style="{ backgroundColor: brandColor }"
      >
        <div class="home-page__sidebar-top">
          <v-img
            class="home-page__logo"
            :src="displayLogo"
            alt="Home"
            max-height="68"
            contain
            @click="navigateHome()"
          />
        </div>

        <div
          class="home-page__sidebar-body"
          :class="sidebarBodySizeClass"
        >
          <div class="home-page__sidebar-head">
            <h2 class="home-page__sidebar-title">Book a Space</h2>
            <div
              class="home-page__sidebar-accent"
              :style="{ backgroundColor: fontColor }"
            ></div>
          </div>

          <nav
            class="home-page__venue-nav"
            :class="venueNavSizeClass"
          >
            <button
              v-for="(config, i) in bookingOptions"
              :key="i"
              type="button"
              class="home-page__venue-btn"
              :class="{ 'home-page__venue-btn--active': activeVenueIndex === i }"
              :style="venueItemStyle(i)"
              @click="onVenueClick(config, i)"
            >
              {{ config.venueType }}
            </button>
          </nav>
        </div>
      </aside>

      <div class="home-page__right">
        <main class="home-page__main">
          <v-img
            v-if="rightLogo"
            class="home-page__partner-logo"
            :src="rightLogo"
            alt=""
            max-height="68"
            contain
          />

          <div
            class="home-page__hero"
            :style="{ backgroundImage: heroImage ? `url('${heroImage}')` : undefined }"
          ></div>
          <div class="home-page__hero-overlay"></div>

          <div class="home-page__content">
            <section class="home-page__welcome home-page__welcome--desktop glass-panel">
              <h2 class="home-page__welcome-title">
                Welcome to
                <span class="home-page__welcome-facility">{{ title }}</span>
              </h2>
              <p class="home-page__welcome-text">{{ welcomeText }}</p>
            </section>

            <section
              v-if="announcements?.length"
              class="home-page__announcements"
            >
              <h3 class="home-page__announcements-title">Announcements</h3>
              <home-notification :announcements="announcements" />
            </section>
          </div>
        </main>
      </div>
    </div>

    <!-- Mobile layout (reference design) -->
    <div class="home-page__layout home-page__layout--mobile">
      <header-component />

      <main class="home-mobile-main">
        <section
          class="home-mobile-hero"
          :style="mobileHeroStyle"
        >
          <div class="home-mobile-hero__overlay" aria-hidden="true"></div>
          <div class="home-mobile-hero__content">
            <h2 class="home-mobile-hero__title">
              Welcome to {{ title }}
            </h2>
            <p class="home-mobile-hero__text">{{ welcomeText }}</p>
          </div>
        </section>

        <section
          v-if="announcements?.length"
          class="home-mobile-announcements"
        >
          <home-notification
            class="home-mobile-announcements__list"
            :announcements="announcements"
          />
        </section>

        <section class="home-mobile-venues">
          <div class="home-mobile-venues__head">
            <h2 class="home-mobile-venues__title">Book a Space</h2>
            <div
              class="home-mobile-venues__accent"
              :style="{ backgroundColor: fontColor }"
            ></div>
          </div>

          <nav class="home-mobile-venues__list">
            <button
              v-for="(config, i) in bookingOptions"
              :key="i"
              type="button"
              class="home-mobile-venue-card"
              :class="{ 'home-mobile-venue-card--active': activeVenueIndex === i }"
              :style="venueItemStyle(i)"
              @click="onVenueClick(config, i)"
            >
              <span class="home-mobile-venue-card__label">{{ config.venueType }}</span>
              <v-icon
                class="home-mobile-venue-card__chevron"
                icon="mdi-chevron-right"
                size="26"
                :color="activeVenueIndex === i ? brandColor : venueListTextColor"
                aria-hidden="true"
              />
            </button>
          </nav>
        </section>
      </main>
    </div>

    <footer-component />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import HomeNotification from "@/components/common/HomeNotification/index.vue";
import HeaderComponent from "@/components/common/HeaderComponent/index.vue";
import FooterComponent from "@/components/common/FooterComponent/index.vue";
import BgImage from "@/assets/home-row-bg-mobile.jpg";
import FCCLOGO from "@/assets/FCCLOGO-light.png";
import {
  getBookingVenueTypeSettings,
  getBookingGeneral,
  getBookingVenueSettings,
} from "../../../store/vuexStore";
import * as ENV from "@/config.js";
import { isEventsVenueVenueType } from "@/utils/eventsVenueMatch.js";
import {
  applyBookingTheme,
  getBrandColor,
  getButtonColor,
  getContrastTextColor,
  getFontColor,
} from "@/utils/bookingTheme.js";
import { isEventsBookingFacility } from "@/constants/eventsFacilityPromo.js";

export default {
  components: { HomeNotification, HeaderComponent, FooterComponent },
  data: () => ({
    routing: "booking",
    title: "Tennis Booking App",
    bookingOptions: [],
    announcements: [],
    storeaddedbookingdetails: [],
    storemultiBookingCartData: [],
    leftLogo: "",
    rightLogo: "",
    bgImage: "",
    activeVenueIndex: null,
    defaultLogo: FCCLOGO,
  }),
  computed: {
    bookingGeneral() {
      const facilityKey = `${ENV.VUE_APP_FACILITY}_booking`;
      return (
        this.$store.state[facilityKey]?.bookingGeneral ||
        this.$store.state.tennis_booking?.bookingGeneral ||
        {}
      );
    },
    brandColor() {
      return getBrandColor(this.bookingGeneral);
    },
    buttonColor() {
      return getButtonColor(this.bookingGeneral);
    },
    fontColor() {
      return getFontColor(this.bookingGeneral);
    },
    venueListTextColor() {
      return getContrastTextColor(this.fontColor);
    },
    venueNavSizeClass() {
      const count = this.bookingOptions.length;
      if (count <= 4) return "home-page__venue-nav--few";
      if (count <= 7) return "home-page__venue-nav--medium";
      return "home-page__venue-nav--many";
    },
    sidebarBodySizeClass() {
      const count = this.bookingOptions.length;
      if (count <= 4) return "home-page__sidebar-body--few";
      if (count > 7) return "home-page__sidebar-body--many";
      return "";
    },
    heroImage() {
      return this.bgImage || "";
    },
    mobileHeroStyle() {
      const image = this.heroImage || BgImage;
      return image ? { backgroundImage: `url('${image}')` } : undefined;
    },
    displayLogo() {
      return this.leftLogo || this.defaultLogo;
    },
    welcomeText() {
      return "Select a venue to begin your booking journey. We provide high-quality facilities across Fairfield City.";
    },
    homeThemeVars() {
      const brand = this.brandColor || "#00666c";
      return {
        "--home-brand": brand,
        "--home-font-color": this.fontColor || brand,
        "--booking-button-color": this.buttonColor,
      };
    },
  },
  watch: {
    bookingGeneral: {
      handler(general) {
        if (!general || !Object.keys(general).length) return;
        applyBookingTheme(general);
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      bookingGetConfig: `${ENV.VUE_APP_FACILITY}_booking/bookingGetConfig`,
      setTempBooking: `${ENV.VUE_APP_FACILITY}_booking/setTempBooking`,
      setMultiBookingData: `${ENV.VUE_APP_FACILITY}_booking/setMultiBookingData`,
    }),
    navigateHome() {
      this.$router.push({ path: `/bookingapp/${ENV.VUE_APP_FACILITY}/` });
    },
    venueItemStyle(index) {
      if (this.activeVenueIndex === index) {
        return {
          background: "#ffffff",
          backgroundColor: "#ffffff",
          color: this.brandColor,
          borderColor: "#bdc9ca",
        };
      }
      return {
        background: this.fontColor,
        backgroundColor: this.fontColor,
        color: this.venueListTextColor,
        borderColor: "rgba(255, 255, 255, 0.2)",
      };
    },
    onVenueClick(config, index) {
      this.activeVenueIndex = index;
      if (config.venueType.startsWith("Multi") || config.venueType.startsWith("Other")) {
        this.navigateToBookingform();
        return;
      }
      this.navigate(this.routing, config);
    },
    navigateToBookingform() {
      this.$router.push({ path: `/bookingapp/endeavourreserve/directbooking` });
    },
    setData() {
      const data = getBookingGeneral() || {};
      const venueTypeSettings = getBookingVenueTypeSettings() || [];
      this.bookingOptions = [];

      for (let index = 0; index < venueTypeSettings.length; index++) {
        const element = venueTypeSettings[index];
        if (element.enablevenuegroup) {
          this.bookingOptions.push(element);
        }
      }

      this.title = data?.header?.title || "Tennis Booking App";
      this.bgImage = data.homepageimage;
      this.leftLogo = data.header?.leftLogo || "";
      this.rightLogo = data.header?.rightLogo || "";
      this.announcements = data.announcements || [];
    },
    navigate(path, venueDetail) {
      sessionStorage.setItem("selectedvenuetype", venueDetail.venueType);
      sessionStorage.setItem("selectedvenuetypeId", venueDetail.venueTypeId);
      const newVenueType = venueDetail.venueType.replaceAll(" ", "_");

      if (
        isEventsBookingFacility() &&
        isEventsVenueVenueType(venueDetail.venueType)
      ) {
        if (venueDetail.enablePrivateVenue) {
          this.$router.push({
            path: `/bookingapp/${ENV.VUE_APP_FACILITY}/booking`,
            query: { venueType: newVenueType },
          });
        } else {
          this.$router.push({
            path: `/bookingapp/${ENV.VUE_APP_FACILITY}/eventsbooking`,
            query: { venueType: newVenueType },
          });
        }
        return;
      }

      path = `${path}/?venueType=${newVenueType}`;
      this.$router.push({ path, query: { venueType: newVenueType } });
    },
  },
  async created() {
    sessionStorage.removeItem("venueBlockId");
    sessionStorage.removeItem("venuetype");
    sessionStorage.removeItem("statusbar");
    sessionStorage.removeItem("starttime");
    sessionStorage.removeItem("endtime");
    sessionStorage.removeItem("enablekey");
    sessionStorage.removeItem("enablebusinesskey");
    sessionStorage.removeItem("BookingType");
    sessionStorage.removeItem("selectedvenuename");
    sessionStorage.removeItem("selectedvenuetype");
    sessionStorage.removeItem("countDownTarget");

    this.storeaddedbookingdetails = [];
    const adminVenues = getBookingVenueSettings() || [];
    sessionStorage.removeItem("isHourlyBooking");
    sessionStorage.removeItem("isFreeBooking");
    sessionStorage.removeItem("isFulldayBooking");

    for (let index = 0; index < adminVenues.length; index++) {
      sessionStorage.removeItem(adminVenues[index].venueName);
    }

    sessionStorage.removeItem("crn2");
    await this.setTempBooking(this.storeaddedbookingdetails);
    this.$store.commit(
      `${ENV.VUE_APP_FACILITY}_booking/setTemporarybookingdetails`,
      this.storeaddedbookingdetails
    );

    this.storemultiBookingCartData = [];
    await this.setMultiBookingData(this.storemultiBookingCartData);
    this.$store.commit(
      `${ENV.VUE_APP_FACILITY}_booking/setMultibookingdetails`,
      this.storemultiBookingCartData
    );

    localStorage.clear();
    await this.bookingGetConfig();

    this.setData();

    if (window.innerWidth <= 600 && !this.bgImage) {
      this.bgImage = BgImage;
    }
  },
};
</script>

<style src="./homepage.css"></style>
